import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useProducts } from "@/hooks/useCatalogue";
import { useAdminProgrammes, adminApi, useAdminMutation, errorMessage, inputCls } from "@/hooks/useAdmin";
import { AdminHeading, Badge } from "@/components/admin/AdminLayout";

const STATUSES = ["draft", "open", "waitlist", "closed", "completed"];
const tone = { draft: "muted", open: "sage", waitlist: "champagne", closed: "muted", completed: "forest" };

const empty = {
  title: "", summary: "", description: "", product_id: "", capacity: "", duration_weeks: 4, cohort_start: "", cohort_end: "",
  eligibility: ["You are 18 or over.", "You are a member of Rituals With You."],
  terms: ["Membership and application do not guarantee a product. Selection depends on eligibility and availability.", "Your check-ins are private by default. Sharing a public story is a separate, voluntary choice.", "You may withdraw at any time from My Rituals."],
  cost_note: "Participation cost: to be confirmed.", shipping_note: "Shipping terms and regions: to be confirmed.",
  questions: [{ id: "routine", label: "Describe your current skincare routine in a sentence or two.", type: "text", required: true, options: [] }],
  checkin_days: [1, 7, 14, 28],
};

function ListEditor({ label, items, onChange, testid }) {
  return (
    <div>
      <p className="text-sm text-charcoal/80 mb-2">{label}</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input value={it} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className={inputCls} data-testid={`${testid}-${i}`} />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-charcoal/40 hover:text-destructive px-2" aria-label="Remove"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...items, ""])} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-forest link-underline" data-testid={`${testid}-add`}><Plus size={12} /> Add</button>
      </div>
    </div>
  );
}

function QuestionsEditor({ questions, onChange }) {
  const upd = (i, patch) => onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  return (
    <div>
      <p className="text-sm text-charcoal/80 mb-2">Application questions</p>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={q.id || `q-${i}`} className="border border-forest/15 p-4 grid sm:grid-cols-[1fr_130px_auto_auto] gap-3 items-start" data-testid={`question-${i}`}>
            <input value={q.label} placeholder="Question" onChange={(e) => upd(i, { label: e.target.value, id: q.id || `q${i + 1}` })} className={inputCls} data-testid={`question-${i}-label`} />
            <select value={q.type} onChange={(e) => upd(i, { type: e.target.value })} className={inputCls}><option value="text">Free text</option><option value="select">Choice</option></select>
            <label className="flex items-center gap-2 text-xs text-charcoal/70 pt-3"><input type="checkbox" checked={q.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Required</label>
            <button type="button" onClick={() => onChange(questions.filter((_, j) => j !== i))} className="text-charcoal/40 hover:text-destructive pt-2.5" aria-label="Remove question"><Trash2 size={14} /></button>
            {q.type === "select" && (
              <input value={(q.options || []).join(", ")} placeholder="Options, comma separated" onChange={(e) => upd(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className={`${inputCls} sm:col-span-4`} />
            )}
          </div>
        ))}
        <button type="button" onClick={() => onChange([...questions, { id: `q${questions.length + 1}`, label: "", type: "text", required: false, options: [] }])} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-forest link-underline" data-testid="question-add"><Plus size={12} /> Add question</button>
      </div>
    </div>
  );
}

function ProgrammeForm({ initial, products, onClose }) {
  const [f, setF] = useState({ ...empty, ...initial, capacity: initial?.capacity ?? "", cohort_start: initial?.cohort_start || "", cohort_end: initial?.cohort_end || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = useAdminMutation(initial?.programme_id ? adminApi.updateProgramme : adminApi.createProgramme, () => { toast.success(initial?.programme_id ? "Programme updated." : "Programme created as draft."); onClose(); });
  const submit = (e) => {
    e.preventDefault();
    if (!f.product_id) { toast.error("Choose a product."); return; }
    const body = {
      ...f, capacity: f.capacity ? Number(f.capacity) : null, duration_weeks: f.duration_weeks ? Number(f.duration_weeks) : null,
      cohort_start: f.cohort_start || null, cohort_end: f.cohort_end || null,
      eligibility: f.eligibility.filter(Boolean), terms: f.terms.filter(Boolean),
      questions: f.questions.filter((q) => q.label.trim()).map((q, i) => ({ ...q, id: q.id || `q${i + 1}` })),
    };
    delete body.programme_id; delete body.slug; delete body.status; delete body.sample; delete body.created_at; delete body.updated_at; delete body.counts;
    save.mutate(initial?.programme_id ? { id: initial.programme_id, body } : body, { onError: (err) => toast.error(errorMessage(err)) });
  };
  return (
    <form onSubmit={submit} className="border border-forest/20 p-1 mb-8" data-testid="programme-form">
      <div className="border border-forest/10 p-6 space-y-5">
        <p className="eyebrow">{initial?.programme_id ? "Edit programme" : "New programme"}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2"><span className="text-sm text-charcoal/80">Title</span><input value={f.title} onChange={set("title")} className={`mt-1.5 ${inputCls}`} data-testid="prog-title" required /></label>
          <label className="block sm:col-span-2"><span className="text-sm text-charcoal/80">Summary (shown on cards)</span><textarea rows={2} value={f.summary} onChange={set("summary")} className={`mt-1.5 ${inputCls}`} data-testid="prog-summary" required /></label>
          <label className="block sm:col-span-2"><span className="text-sm text-charcoal/80">Description</span><textarea rows={3} value={f.description} onChange={set("description")} className={`mt-1.5 ${inputCls}`} data-testid="prog-description" /></label>
          <label className="block"><span className="text-sm text-charcoal/80">Product</span>
            <select value={f.product_id} onChange={set("product_id")} className={`mt-1.5 ${inputCls}`} data-testid="prog-product"><option value="">Choose…</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          <label className="block"><span className="text-sm text-charcoal/80">Places (capacity)</span><input type="number" min="1" value={f.capacity} onChange={set("capacity")} className={`mt-1.5 ${inputCls}`} data-testid="prog-capacity" /></label>
          <label className="block"><span className="text-sm text-charcoal/80">Duration (weeks)</span><input type="number" min="1" value={f.duration_weeks} onChange={set("duration_weeks")} className={`mt-1.5 ${inputCls}`} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-sm text-charcoal/80">Cohort start</span><input type="date" value={f.cohort_start} onChange={set("cohort_start")} className={`mt-1.5 ${inputCls}`} /></label>
            <label className="block"><span className="text-sm text-charcoal/80">Cohort end</span><input type="date" value={f.cohort_end} onChange={set("cohort_end")} className={`mt-1.5 ${inputCls}`} /></label>
          </div>
          <label className="block"><span className="text-sm text-charcoal/80">Cost note</span><input value={f.cost_note} onChange={set("cost_note")} className={`mt-1.5 ${inputCls}`} /></label>
          <label className="block"><span className="text-sm text-charcoal/80">Shipping note</span><input value={f.shipping_note} onChange={set("shipping_note")} className={`mt-1.5 ${inputCls}`} /></label>
        </div>
        <ListEditor label="Eligibility criteria" items={f.eligibility} onChange={(v) => setF({ ...f, eligibility: v })} testid="elig" />
        <ListEditor label="Participation terms" items={f.terms} onChange={(v) => setF({ ...f, terms: v })} testid="term" />
        <QuestionsEditor questions={f.questions} onChange={(v) => setF({ ...f, questions: v })} />
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button type="submit" disabled={save.isPending} className="btn btn-primary disabled:opacity-60" data-testid="prog-save">{save.isPending ? "Saving…" : "Save programme"}</button>
          <button type="button" onClick={onClose} className="btn btn-outline">Cancel</button>
        </div>
      </div>
    </form>
  );
}

export default function AdminProgrammes() {
  const { data: programmes = [] } = useAdminProgrammes();
  const { data: products = [] } = useProducts();
  const [editing, setEditing] = useState(null); // null | {} | programme
  const setStatus = useAdminMutation(adminApi.setProgrammeStatus, () => toast.success("Programme status updated."));

  return (
    <div data-testid="admin-programmes">
      <AdminHeading eyebrow="Manage" title="Programmes">
        <button onClick={() => setEditing({})} className="btn btn-primary !px-5 !py-3" data-testid="new-programme-button"><Plus size={14} /> New programme</button>
      </AdminHeading>
      {editing && <ProgrammeForm key={editing.programme_id || "new"} initial={editing} products={products} onClose={() => setEditing(null)} />}
      <div className="space-y-3">
        {programmes.map((p) => (
          <div key={p.programme_id} className="border border-forest/15 bg-cream p-5 grid md:grid-cols-[1fr_auto] gap-4" data-testid={`admin-programme-${p.slug}`}>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={tone[p.status]}>{p.status}</Badge>
                {p.sample && <Badge>Sample</Badge>}
                <span className="text-[10px] uppercase tracking-[0.2em] text-charcoal/45">{products.find((x) => x.id === p.product_id)?.name} · /programmes/{p.slug}</span>
              </div>
              <h3 className="mt-2 font-serif text-2xl text-forest font-light">{p.title}</h3>
              <p className="mt-1 text-sm text-charcoal/60 font-light">{p.summary}</p>
              <p className="mt-2 text-xs text-charcoal/50">
                {Object.entries(p.counts || {}).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ") || "No applications yet"}
              </p>
            </div>
            <div className="flex md:flex-col gap-2 items-start md:items-end">
              <select value={p.status} onChange={(e) => setStatus.mutate({ id: p.programme_id, status: e.target.value }, { onError: (err) => toast.error(errorMessage(err)) })} className={`${inputCls} !w-auto`} data-testid={`prog-status-${p.slug}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => setEditing(p)} className="text-[11px] uppercase tracking-[0.2em] text-forest link-underline" data-testid={`edit-programme-${p.slug}`}>Edit</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
